import React from 'react';
import {useParams} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {getDocumentType, useIVStore} from '@chekin/shared';
import {useIVComputedDetails} from './useIVComputedDetails';
import {IV_DOCUMENT_IMAGE_TYPES, IDENTITY_VERIFICATION_TYPES} from '@chekin/core';

type Props = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
};
function useGetNextStepAfterIV({docType, onlyScanning}: Props) {
  const {id} = useParams();
  const documentType = useIVStore(getDocumentType);
  const {biomatch} = useIVComputedDetails();

  const paths = React.useMemo(() => {
    return {
      selfie: `../${PATHS.verification.selfieInstructions}`,
      back: `../${PATHS.verification.uploadingType}/${IV_DOCUMENT_IMAGE_TYPES.back}`,
      guestsList: `../${PATHS.guestRegistration}`,
      addPersonalDataForm: id
        ? `../../${PATHS.addPersonalDataForm}/${id}`
        : `../../${PATHS.addPersonalDataForm}`,
    };
  }, [id]);

  const getNextStepDetails = React.useCallback(() => {
    if (onlyScanning) {
      return {
        path: paths.addPersonalDataForm,
        type: IDENTITY_VERIFICATION_TYPES.ocr,
        side: IV_DOCUMENT_IMAGE_TYPES.back,
        completed: true,
      };
    }

    switch (docType) {
      case IV_DOCUMENT_IMAGE_TYPES.front:
        if (documentType?.two_sides) {
          return {
            path: paths.back,
            side: IV_DOCUMENT_IMAGE_TYPES.front,
            completed: false,
          };
        } else {
          if (biomatch) {
            return {
              path: paths.selfie,
              type: IDENTITY_VERIFICATION_TYPES.biomatch,
              side: IV_DOCUMENT_IMAGE_TYPES.front,
              completed: false,
            };
          } else {
            return {
              path: paths.addPersonalDataForm,
              type: IDENTITY_VERIFICATION_TYPES.ocr,
              side: IV_DOCUMENT_IMAGE_TYPES.front,
              completed: true,
            };
          }
        }
      case IV_DOCUMENT_IMAGE_TYPES.back:
        if (biomatch) {
          return {
            path: paths.selfie,
            type: IDENTITY_VERIFICATION_TYPES.biomatch,
            side: IV_DOCUMENT_IMAGE_TYPES.back,
            completed: false,
          };
        }
        return {
          path: paths.addPersonalDataForm,
          type: IDENTITY_VERIFICATION_TYPES.ocr,
          side: IV_DOCUMENT_IMAGE_TYPES.back,
          completed: true,
        };
      case IV_DOCUMENT_IMAGE_TYPES.selfie:
        return {
          path: paths.addPersonalDataForm,
          type: IDENTITY_VERIFICATION_TYPES.biomatch,
          side: IV_DOCUMENT_IMAGE_TYPES.selfie,
          completed: true,
        };
      default:
        return {
          path: paths.addPersonalDataForm,
          completed: true,
        };
    }
  }, [
    onlyScanning,
    biomatch,
    docType,
    documentType?.two_sides,
    paths.addPersonalDataForm,
    paths.back,
    paths.selfie,
  ]);

  return {getNextStepDetails};
}

export {useGetNextStepAfterIV};
